import { useEffect, useMemo } from "react";
import { Box, Divider, IconButton, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import { useTranslation } from "react-i18next";
import {
  colorActionPrimary,
  colorActionPrimaryText,
  colorBorder,
  colorSurface,
  colorTextMuted,
  colorTextPrimary,
  colorTextSecondary,
  radiusCard,
  radiusInput,
  shadowCard,
} from "src/design/tokens";
import { CustomButton } from "src/design/CustomButton";

const MIN_GUESTS = 1;
/** Quick-select buttons offered when the restaurant sets no cap of its own. */
const DEFAULT_QUICK_SELECT_COUNT = 6;
/** Past this the grid grows taller than the card, so the stepper takes over. */
const MAX_QUICK_SELECT_COUNT = 12;

interface Props {
  guestCount: number;
  onChangeGuestCount: (n: number) => void;
  onContinue: () => void;
  onBack: () => void;
  phone: string;
  /**
   * Largest party the restaurant accepts on a single booking. `null` when it
   * sets no limit — the stepper is then uncapped and the quick select falls
   * back to its default six buttons.
   */
  maximumPartySize: number | null;
}

const GuestSelection = ({
  guestCount,
  onChangeGuestCount,
  onContinue,
  onBack,
  phone,
  maximumPartySize,
}: Props) => {
  const { t } = useTranslation();

  // One button per acceptable party size, so a restaurant that takes more than
  // six never hides those sizes behind the stepper — capped at twelve, past
  // which the grid outgrows the card.
  const quickSelect = useMemo(() => {
    const count =
      maximumPartySize === null
        ? DEFAULT_QUICK_SELECT_COUNT
        : Math.max(
            MIN_GUESTS,
            Math.min(MAX_QUICK_SELECT_COUNT, maximumPartySize),
          );
    return Array.from({ length: count }, (_, index) => index + 1);
  }, [maximumPartySize]);

  const atMaximum = maximumPartySize !== null && guestCount >= maximumPartySize;

  // The page starts at two guests, which a restaurant capping parties at one
  // would never accept: pull an out-of-range count back down to the cap.
  useEffect(() => {
    if (maximumPartySize !== null && guestCount > maximumPartySize) {
      onChangeGuestCount(Math.max(MIN_GUESTS, maximumPartySize));
    }
  }, [maximumPartySize, guestCount, onChangeGuestCount]);

  const decrement = () => onChangeGuestCount(Math.max(MIN_GUESTS, guestCount - 1));
  const increment = () => {
    const next = guestCount + 1;
    onChangeGuestCount(
      maximumPartySize === null ? next : Math.min(maximumPartySize, next),
    );
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          mb: 1.5,
          cursor: "pointer",
          width: "fit-content",
          color: colorTextMuted,
          "&:hover": { color: colorTextPrimary },
        }}
        onClick={onBack}
      >
        <ArrowBackIcon sx={{ fontSize: 18 }} />
        <Typography variant="body2" fontWeight={500}>
          {t("clientBooking.back")}
        </Typography>
      </Box>

      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
        {t("clientBooking.numGuests")}
      </Typography>
      <Typography variant="body2" color={colorTextMuted} sx={{ mb: 2.5 }}>
        {t("clientBooking.numGuestsSubtitle")}
      </Typography>

      <Box
        sx={{
          backgroundColor: colorSurface,
          border: `1px solid ${colorBorder}`,
          borderRadius: radiusCard,
          boxShadow: shadowCard,
          p: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 2.5,
          }}
        >
          <PeopleAltOutlinedIcon
            sx={{ fontSize: 36, color: colorTextMuted, mb: 1.5 }}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 3,
            }}
          >
            <IconButton
              onClick={decrement}
              disabled={guestCount <= MIN_GUESTS}
              sx={{
                border: `1px solid ${colorBorder}`,
                borderRadius: radiusInput,
                width: 40,
                height: 40,
                "&:hover": { borderColor: colorTextMuted },
              }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>

            <Box sx={{ textAlign: "center", minWidth: 48 }}>
              <Typography variant="h4" fontWeight={700}>
                {guestCount}
              </Typography>
              <Typography variant="caption" color={colorTextMuted}>
                {t("clientBooking.guests")}
              </Typography>
            </Box>

            <IconButton
              onClick={increment}
              disabled={atMaximum}
              sx={{
                border: `1px solid ${colorBorder}`,
                borderRadius: radiusInput,
                width: 40,
                height: 40,
                "&:hover": { borderColor: colorTextMuted },
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Typography
          variant="body2"
          color={colorTextSecondary}
          fontWeight={500}
          sx={{ mb: 1.25 }}
        >
          {t("clientBooking.quickSelect")}
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 1,
            mb: 2.5,
          }}
        >
          {quickSelect.map((n) => {
            const selected = guestCount === n;
            return (
              <Box
                key={n}
                onClick={() => onChangeGuestCount(n)}
                sx={{
                  border: `1px solid ${selected ? colorActionPrimary : colorBorder}`,
                  borderRadius: radiusInput,
                  p: 1.25,
                  textAlign: "center",
                  cursor: "pointer",
                  backgroundColor: selected ? colorActionPrimary : colorSurface,
                  color: selected ? colorActionPrimaryText : colorTextPrimary,
                  fontWeight: selected ? 700 : 400,
                  transition: "all 0.15s",
                  "&:hover": {
                    borderColor: colorActionPrimary,
                  },
                }}
              >
                <Typography
                  variant="body1"
                  fontWeight="inherit"
                  color="inherit"
                >
                  {n}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <CustomButton
          color="black"
          fullWidth
          sx={{ borderRadius: radiusInput, py: 1.25 }}
          onClick={onContinue}
        >
          {t("clientBooking.continueCta")}
        </CustomButton>
      </Box>

      <Typography
        variant="body2"
        color={colorTextMuted}
        textAlign="center"
        sx={{ mt: 2, px: 2 }}
      >
        {maximumPartySize === null
          ? t("clientBooking.largePartyNoteNoLimit", { phone })
          : t("clientBooking.largePartyNote", { phone, max: maximumPartySize })}
      </Typography>
    </Box>
  );
};

export default GuestSelection;
